import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useLocation } from "react-router-dom";

import useQueryBoard from "../../lib/hooks/useQueryBoard";

interface AcceptedAgreementsValue {
  /** Ids of the documents this person has gone through, in order. */
  accepted: string[];
  accept: (id: string) => void;
  isAccepted: (id: string) => boolean;
}

export const AgreementContext = React.createContext<AcceptedAgreementsValue>(
  null!,
);

export const useAcceptedAgreements = () => useContext(AgreementContext);

/**
 * A check-in can show its documents in two places: the ones meant for everybody
 * go up before the code is entered, the rest only once we know who the person
 * is. Those are different pages, so what has been accepted is kept here for the
 * length of one check-in — and it is this list that the mutation sends, so
 * nothing is recorded as signed that was not actually shown.
 *
 * Going back to the home screen ends that check-in and clears the list.
 */
export const AgreementProvider = React.memo<{ children: React.ReactNode }>(
  (props) => {
    const { children } = props;

    const { pathname } = useLocation();

    const { data } = useQueryBoard();

    const [accepted, setAccepted] = useState<string[]>([]);

    useEffect(() => {
      if (pathname === "/") {
        setAccepted((previous) => (previous.length ? [] : previous));
      }
    }, [pathname]);

    const agreementFiles = data?.digitalReception?.entranceAgreement?.files;

    /**
     * Warm the rendered pages while the screen is idle, so the dialog paints
     * from cache the moment someone checks in. The service worker keeps them
     * afterwards, which is also what makes the dialog work offline.
     */
    useEffect(() => {
      (agreementFiles ?? []).forEach((file) => {
        (file.file?.pages ?? []).forEach((page) => {
          if (!page.absolutePath) {
            return;
          }

          const image = new Image();
          image.src = page.absolutePath;
        });
      });
    }, [agreementFiles]);

    const accept = useCallback((id: string) => {
      setAccepted((previous) =>
        previous.includes(id) ? previous : [...previous, id],
      );
    }, []);

    const isAccepted = useCallback(
      (id: string) => accepted.includes(id),
      [accepted],
    );

    const value = useMemo(
      (): AcceptedAgreementsValue => ({ accepted, accept, isAccepted }),
      [accepted, accept, isAccepted],
    );

    return (
      <AgreementContext.Provider value={value}>
        {children}
      </AgreementContext.Provider>
    );
  },
);
